import {AnyAction} from 'redux'
import {ApiConnection} from 'powtoon-api/src/apiConnection'
import {stringifySortingOptions, ISortingOption} from 'powtoon-api/src/utils'
import {EmptyDocumentSkeleton} from 'powtoon-document/types'
import {
  Powtoon, PowtoonId, PowtoonsAPIResponse, DeletePowtoonAPIResponse, PublishDataAPIResponse, CollaborationAPIResponse,
  PublishVideoArgs, TranslatePowtoonParams, UtmCampaign, SharingPermissions, PowtoonWithContent
} from './types'
import {UserId} from 'powtoon-services/current-user/types'
import {FIRST_PAGE_SIZE} from './consts'
import {IListDataServerResponse} from 'powtoon-commons/reduxThunkUtils/fetched-data/types'
import {ExportsDataApiResponse} from 'powtoon-services/powtoons/types'
import {getCurrentPageUrlParams} from 'powtoon-commons/utils/urls'
import {createEmptyDocument} from 'powtoon-document/consts/document'
import {createDocumentFormData} from 'powtoon-document/document'
import {FLOW_TYPES} from 'powtoon-services/navigation/components/MainMenu/types'
import {Dictionary, keyBy} from 'lodash'
import {PublicUserProfile} from 'powtoon-services/current-user'
import {updatePowtoonContent} from 'powtoon-versioning'
// @ts-expect-error in import
import JSON_ from 'json_'

interface SearchMyPowtoons {
  searchText: string
}

export const getMyPowtoons =
  (connection: ApiConnection) => (includeShared: boolean = true, pageSize = FIRST_PAGE_SIZE) => {
    const searchParams = new URLSearchParams()
    searchParams.set('page_size', `${pageSize}`)
    searchParams.set('include_shares', `${includeShared}`)
    const powtoonIds = getCurrentPageUrlParams().get('powtoon_ids') ||
    getCurrentPageUrlParams(window.location.hash.split('?')[1]).get('powtoon_ids')

    if (powtoonIds) {
      searchParams.set('powtoon_ids', powtoonIds)
    }

    return connection.get<IListDataServerResponse<PowtoonsAPIResponse>>(
      `powtoons?${searchParams.toString()}`
    )
  }

export const getPowtoon = (connection: ApiConnection) => (powtoonId: PowtoonId) => {
  return connection.get<IListDataServerResponse<PowtoonsAPIResponse>>(`powtoons/${powtoonId}`)
}

export const getDocument = (connection: ApiConnection) => (powtoonId: string) => {
  return connection.get(`powtoons/${powtoonId}?include_content=true`,
    {includeResponseStatus: true, processJsonResponse: false})
    .then(async ({value: document}): Promise<PowtoonWithContent> => {
      const origin = JSON.parse(document)
      const camel = JSON_.parse(document)
      const content = await updatePowtoonContent(origin.content)
      return {...camel, content}
    })
}

export const saveDocument = (connection: ApiConnection) =>
  (document: Powtoon, updateThumbnail = true) => {
    const body = createDocumentFormData(document, {shouldUpdateExistingThumbnail: updateThumbnail})
    return connection.put(`powtoons/${document.id}`, body, {isFormData: true, includeResponseStatus: true})
  }

export const searchMyPowtoons = (connection: ApiConnection) => ({searchText}: SearchMyPowtoons) => {
  return connection.get<PowtoonsAPIResponse>(`powtoons?search=${encodeURIComponent(searchText)}`)
}

export const createPowtoon = (connection: ApiConnection) =>
  ({aspectRatio = 'none', flowType}: { aspectRatio?: string, flowType?: string }) => {

    const created = flowType ? {created: {flow: flowType}} : null
    const document = createEmptyDocument(aspectRatio, created)
    const documentFormData = createDocumentFormData(document)

    return connection.post<Powtoon>('powtoons', documentFormData, {isFormData: true})
      .then(powtoon => ({id: powtoon.id}))
  }

export const createGeneratedPowtoon = (connection: ApiConnection) =>
  (content: EmptyDocumentSkeleton) => {
    const body = createDocumentFormData(content)
    return connection.post('powtoons', body, {isFormData: true})
  }

export const deletePowtoon = (connection: ApiConnection) => (powtoon: Powtoon) => {
  return connection.delete<DeletePowtoonAPIResponse>(`powtoons/${powtoon.id}`, {processJsonResponse: true})
}

export const deleteBunchPowtoons = (connection: ApiConnection) => (powtoonsIds: string[]) => {
  return connection.delete<DeletePowtoonAPIResponse>('powtoons', {processJsonResponse: true, body: powtoonsIds})
}

export const duplicatePowtoon = (connection: ApiConnection) => (powtoon: Powtoon) => {
  return connection.post<Powtoon>(`powtoons/${powtoon.id}/duplicate`)
}

export const updatePowtoon = (connection: ApiConnection) => (powtoon: Partial<Powtoon> & { id: PowtoonId }) => {

  // todo unify with createDocumentFormData later, which is used in studio
  const powtoonFormData = new FormData()

  powtoonFormData.append('id', powtoon.id)

  if (powtoon.name) {
    powtoonFormData.append('name', powtoon.name)
  }

  return connection.patch<Powtoon>(`powtoons/${powtoon.id}`, powtoonFormData, {isFormData: true})
}

export const getPublishData = (connection: ApiConnection) => (powtoonId: PowtoonId) => {
  return connection.get<PublishDataAPIResponse>(`powtoons/${powtoonId}/publish-data`)
}

export const getPowtoonExports = (connection: ApiConnection) => (powtoonId: string) => {
  return connection.get<ExportsDataApiResponse>(`powtoons/${powtoonId}/exports`)
}

export const publishVideo = (connection: ApiConnection) => (
  powtoonId: PowtoonId, publishVideoArgs: PublishVideoArgs
) => {
  return connection.post<ExportsDataApiResponse>(`powtoons/${powtoonId}/exports`, publishVideoArgs)
}

export const importPowtoonFromPPTX = (connection: ApiConnection) => (
  file: File, progressCallbackFunction: (progress: ProgressEvent) => AnyAction | null, creationFlow?: FLOW_TYPES
) => {
  return connection.uploadFiles({
    blob: file, filename: file.name, isPPTXImport: true, onProgressCallback: progressCallbackFunction, creationFlow
  })
}

export const getCollaborationsInvitations = (connection: ApiConnection) => () => {
  return connection.get<CollaborationAPIResponse>('collaborations')
}

export const acknowledgeCollaborationsInvitation = (connection: ApiConnection) => (collabId: string) => {
  return connection.patch<CollaborationAPIResponse>(`collaborations/${collabId}`, {is_acknowledged: true})
}

export const sendCollaborationsInvitations = (connection: ApiConnection) => (
  powtoonId: PowtoonId, emails: string[], captchaResponse?: string
) => {

  return connection.post<object>(
    `powtoons/${powtoonId}/collaborations`,
    {emails},
    {captchaResponse, processJsonResponse: false}
  )
}

export const consumeCredit = (connection: ApiConnection) => (powtoonId: string) => {
  return connection.post('consume-credit/', {powtoon_id: powtoonId})
}

export const prepareTranslation = (connection: ApiConnection) => (powtoonId: string, language: string) =>
  connection.get(`powtoons/translate?powtoon=${powtoonId}&language=${language}`)

export const provideTranslation = (connection: ApiConnection) =>
  (translationFile: File, params?: TranslatePowtoonParams) =>
    connection.uploadFiles({
      blob: translationFile,
      path: 'powtoons/translate',
      params
    })

export const getPowtoonAccessInfo = (connection: ApiConnection) => (powtoonId: string) => {
  return connection.get(`powtoons/${powtoonId}/access`)
}

export const getPowtoonsShares = (connection: ApiConnection) => (powtoonId: string, sortOptions?: ISortingOption) => {
  return connection.get(`powtoons/${powtoonId}/shares?${stringifySortingOptions(sortOptions)}`)
}

export const createPowtoonsShare = (connection: ApiConnection) => (
  powtoonId: string, {granteeId, name, teamName, teamId}: {
    granteeId?: string, name?: string, teamName?: string, teamId?: string
  }
) => {
  return connection.post(`powtoons/${powtoonId}/shares`, {
    org_provider_slug: 'office365',
    grantee_id: granteeId,
    permissions: ['view', 'edit'],
    grantee_type: 'group',
    extra_data: {
      name,
      team_name: teamName,
      team_id: teamId
    }
  })
}

export const getPowtoonAccessPermissions = (connection: ApiConnection) => (powtoonId: string) => {
  return connection.get(`powtoons/${powtoonId}/shares/get-permissions`)
}

const usersArrayToDictionary =
  (usersArray: PublicUserProfile[]): Dictionary<PublicUserProfile> =>
    keyBy(usersArray, 'id')


export const getPowtoonUsers = (connection: ApiConnection) => (powtoonId: string) => {
  return connection.get<PublicUserProfile[]>(`powtoons/${powtoonId}/users`).then(usersArrayToDictionary)
}

// TODO should be moved to powtoon-services/users (and probably merge with current-user)
export const getUsersById = (connection: ApiConnection) => (userIds: string[]): Promise<any> =>
  connection.get<IListDataServerResponse<PublicUserProfile>>(
    `users?${userIds.map(id => `id=${id}`).join('&')}`
  ).then(res => usersArrayToDictionary(res.results))


export const addAccessForPowtoon = (connection: ApiConnection) => (
  powtoonId: string, groupsIds: string[], notifyTeam: boolean
) => {
  const formData = new FormData()

  formData.append('group_ids', groupsIds.join(','))
  if (notifyTeam) {
    formData.append('notify', 'true')
  }

  return connection.put(`powtoons/${powtoonId}/access`, formData, {isFormData: true})
}

export const removeAccessToPowtoon = (connection: ApiConnection) => (powtoonId: string, shareId: string) => (
  connection.delete(`powtoons/${powtoonId}/shares/${shareId}`)
)

export const removeAccessFromPowtoon = (connection: ApiConnection) => (powtoonId: string, groupIds: string[]) => (
  connection.delete(`powtoons/${powtoonId}/access`, {body: {group_ids: groupIds.join(',')}, processJsonResponse: false})
)

export const sendSharePowtoonInvitation = (connection: ApiConnection) => (
  powtoonId: string, emails: string[],
  permissions: SharingPermissions[] = [SharingPermissions.Edit, SharingPermissions.View], utmCampaign?: UtmCampaign
) => {

  const utmTerm = permissions[0]
  const queryParams = utmCampaign ? `?utm_campaign=${utmCampaign}&utm_term=${utmTerm}` : ''
  const promises = emails.map(email => connection.post(
    `powtoons/${powtoonId}/shares/create-by-email${queryParams}`,
    {email, permissions}
  ).then((resp) => resp).catch(err => {
    return {error: {[JSON.parse(err.responseBody).error]: email}}
  }))

  return Promise.all(promises).then((values) => {
    const errors = values.filter((value: {
              error?: {
                  [key: string]: string[]
              }
          }) => !!value.error)
    if (errors.length) {
      const errorObj = errors.reduce((acc: {
                  [key: string]: string[]
              }, {error}) => {
        const errorKey = Object.keys(error)[0]
        if (!acc[errorKey]) {
          acc[errorKey] = [error[errorKey]]
        } else {
          acc[errorKey].push(error[errorKey])
        }
        return acc
      }, {})
      return Promise.reject(errorObj)
    }
    return Promise.resolve(values)
  })
}

export const getClosedCaptionsLocales = (connection: ApiConnection) => () => (
  connection.get('services/captions/languages')
)

export const fetchPublicLink = (connection: ApiConnection) => (powtoonId: PowtoonId) => (
  connection.post(`powtoons/${powtoonId}/public-tokens`)
)

export const setPowtoonOwner = (connection: ApiConnection) => (
  ownerId: UserId,
  powtoonId: PowtoonId
) => (
  connection.put(`powtoons/${powtoonId}/owner`, {ownerId} )
)

export const getTags  = (connection: ApiConnection) => () => (
  connection.get('powtoons/tag-types')
)
